import BaseQuizGame from "./BaseQuizGame.js";
import { renderFlagDuelError } from "../utils/FlagUtils.js";
import { focusBox, readWorldPaths } from "../utils/SvgShapes.js";
import { zoneOf } from "../utils/Continents.js";

/**
 * Silhouette d'un pays, cadrée sur son territoire principal (voir SvgShapes).
 */
export function renderShape(code, paths, cache) {
	const d = paths[code];
	if (!d) return "";
	if (!cache[code]) cache[code] = focusBox(d);
	return `<svg class="shape" viewBox="${cache[code]}" role="img" aria-label="?" preserveAspectRatio="xMidYMid meet"><path d="${d}"/></svg>`;
}

/**
 * Archipels sans île dominante : le cadre de `focusBox()` (l'île principale et
 * ses voisines) n'en montre qu'une partie, méconnaissable. Mesuré sur les pays
 * jouables : Cap-Vert 31 % de la surface dans le cadre, Bahamas 56 %, tous les
 * autres au-delà de 90 %. Les micro-États dispersés (Kiribati, Micronésie,
 * Tuvalu…) sont déjà hors de `easyCountries`.
 */
export const UNRECOGNIZABLE_SHAPES = new Set(["cv", "bs"]);

/**
 * Micro-États absents de Map Nerd (invisibles sur la carte du monde) mais dont la
 * silhouette se reconnaît : île ou territoire d'un seul tenant, ou archipel assez
 * compact (73 % à 100 % du pays dans le cadre), au contour précis
 * (scripts/build-hd-shapes.mjs). Restent exclus les archipels dispersés
 * (Marshall, Kiribati, Maldives, Micronésie, Seychelles, Tonga, Tuvalu,
 * Antigua-et-Barbuda) et Nauru, un disque sans forme.
 */
export const SHAPE_EXTRA_CODES = new Set([
	"mc", "fj", "ws", "mu", "gd", "dm", "lc", "kn", "vc", "pw", // d'un seul tenant
	"sb", "st", "vu", "km", // archipels compacts
]);

/** Pays dont la silhouette se joue : un tracé, et pas un archipel éclaté. */
export const isShapePlayable = (code, paths) => !!paths[code] && !UNRECOGNIZABLE_SHAPES.has(code);

/**
 * Tracés lus dans le SVG monde caché de la page, et pays jouables : ceux de la
 * carte (`easyCountries`, sans les micro-États invisibles à cette échelle) plus
 * SHAPE_EXTRA_CODES, dont la silhouette se reconnaît. Commun à Shape Nerd et Shape Nerd Hard.
 */
export function loadShapes(page) {
	const paths = readWorldPaths(page);
	const easy = new Set(geoNerdApp.easyCountries.map(c => c.code));
	const candidates = geoNerdApp.countries.filter(c => easy.has(c.code) || SHAPE_EXTRA_CODES.has(c.code));
	return { paths, boxes: {}, pool: candidates.filter(c => isShapePlayable(c.code, paths)) };
}

/** La silhouette seule : la question est le h1 de la page, comme dans Flag Nerd. */
export function renderShapeQuestion(country, shapes) {
	return renderShape(country.code, shapes.paths, shapes.boxes);
}

/**
 * Shape Nerd (54 du TODO, Lab) : reconnaître un pays à sa silhouette, en QCM
 * à trois vies. Les propositions viennent du même continent (sinon un contour
 * africain parmi trois pays d'Europe se devine sans le reconnaître), et aucun
 * drapeau n'est montré pendant la question : il donnerait la réponse.
 *
 * Seuls les pays de la carte jouable (`easyCountries`, sans les micro-États
 * invisibles à cette échelle) sont proposés.
 */
export default class ShapeNerd extends BaseQuizGame {
	constructor() {
		super("shapenerd", ".shape-nerd", {
			labMode: "shapenerd",
			gameNameKey: "lab.shapeNerd",
			modeLabelKey: "home.classic",
			mode: "classic",
			shareEmoji: "🟪",
			slug: "shape-nerd",
		});
		if (!this.page) return;
		this.shapes = loadShapes(this.page);
		this.init();
	}

	initCountries() {
		super.initCountries(this.shapes.pool);
	}

	getDistractorPool() {
		const zone = zoneOf(this.rightAnswer.code);
		const { pool } = this.shapes;
		const sameZone = pool.filter(c => zoneOf(c.code) === zone);
		return sameZone.length >= 4 ? sameZone : pool;
	}

	getQuestionFlagCodes() {
		return [];
	}

	renderQuestion(country) {
		return renderShapeQuestion(country, this.shapes);
	}

	renderErrorItem(error) {
		return renderFlagDuelError(error);
	}
}
